import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { X } from "lucide-mui";
import { useTheme } from "../context/ThemeContext";

export function FieldNotesModal({
  open,
  stepNumber,
  stepName,
  initialNote,
  onAdd,
  onClose,
}) {
  const { isDark } = useTheme();
  const [note, setNote] = useState(initialNote ?? "");

  useEffect(() => {
    if (open) setNote(initialNote ?? "");
  }, [open]);

  if (!open) return null;

  const handleAdd = () => {
    onAdd?.(note);
    onClose?.();
  };

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        bgcolor: "rgba(22,24,26,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={{
          width: "504px",
          borderRadius: "12px",
          backgroundImage: isDark
            ? "linear-gradient(167.1deg, rgba(49,54,59,0.98) 6.19%, rgba(30,32,34,0.98) 91.81%)"
            : "linear-gradient(167.1deg, rgba(248,249,250,0.98) 6.19%, rgba(230,234,238,0.98) 91.81%)",
          border: "1px solid",
          borderColor: isDark ? "rgba(106,112,121,0.5)" : "#d5d9df",
          px: "32px",
          py: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {/* Header section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Title + close */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "-0.4492px",
                color: isDark ? "#FFFFFF" : "#31363b",
              }}
            >
              Field Notes
            </Typography>
            <Box
              onClick={onClose}
              sx={{
                width: "36px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: "8px",
                "&:hover": {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                },
              }}
            >
              <X
                sx={{
                  width: "18px",
                  height: "18px",
                  color: isDark ? "#A8AEB6" : "#6A7079",
                }}
              />
            </Box>
          </Box>

          {/* Step label + name */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "14px",
                lineHeight: "16px",
                color: "#9299a1",
                textTransform: "uppercase",
              }}
            >
              Step {stepNumber}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "28px",
                letterSpacing: "-0.4492px",
                color: isDark ? "#FFFFFF" : "#31363b",
              }}
            >
              {stepName || ""}
            </Typography>
          </Box>

          {/* Note textarea */}
          <Box
            component='textarea'
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder='Add a note...'
            rows={3}
            sx={{
              width: "100%",
              minHeight: "71px",
              background: isDark
                ? "#282b2e"
                : "linear-gradient(170.83deg, rgba(168,174,182,0.1) 0%, rgba(126,132,141,0.1) 100%)",
              border: "1px solid",
              borderColor: isDark
                ? "rgba(236,239,243,0.2)"
                : "rgba(49,54,59,0.15)",
              borderRadius: "12px",
              p: "12px",
              fontFamily: "Inter",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.1504px",
              color: isDark ? "#FFFFFF" : "#434950",
              resize: "none",
              outline: "none",
              boxSizing: "border-box",
              "&::placeholder": {
                color: isDark ? "#F1F4F880" : "#A8AEB6",
                fontStyle: "italic",
              },
              "&:focus": {
                borderColor: isDark
                  ? "rgba(236,239,243,0.35)"
                  : "rgba(49,54,59,0.3)",
              },
            }}
          />
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: "16px", justifyContent: "flex-end" }}>
          <Box
            onClick={onClose}
            sx={{
              backgroundImage: isDark
                ? "linear-gradient(157.52deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 50%, rgba(49,54,59,0.8) 100%)"
                : "linear-gradient(157.52deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
              border: "1px solid",
              borderColor: isDark ? "rgba(106,112,121,0.5)" : "#d5d9df",
              borderRadius: "12px",
              px: "20px",
              py: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.021px",
                color: isDark ? "#eceff3" : "#434950",
                userSelect: "none",
              }}
            >
              Cancel
            </Typography>
          </Box>
          <Box
            onClick={handleAdd}
            sx={{
              backgroundImage:
                "linear-gradient(152.1deg, rgba(168,174,182,0.5) 0%, rgba(146,153,161,0.5) 50%, rgba(126,132,141,0.5) 100%)",
              border: "1px solid",
              borderColor: isDark
                ? "rgba(236,239,243,0.4)"
                : "rgba(49,54,59,0.2)",
              borderRadius: "12px",
              px: "20px",
              py: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.021px",
                color: isDark ? "#eceff3" : "#434950",
                userSelect: "none",
              }}
            >
              Add
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
